import { useEffect, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { useGetTrackedCartQuery, useFilteredProductsQuery, GqlProducts } from 'types/graphql';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { useItemsUnavailableModal } from 'src/components/modals/items-unavailable';
import { restoreCartFromTrackedData, restoreCartItems, getProductIdsFromOrderData } from './cart-restoration-utils';
import useUI from './use-ui';
import useCart from './use-cart';
import useUser from './use-user';

export function useCheckoutTokenRestoration(): { loading: boolean; error: any; hasAttemptedRestore: boolean } {
  const router = useRouter();
  const { checkoutToken } = router?.query ?? {};
  const [hasAttemptedRestore, setHasAttemptedRestore] = useState(false);

  const cart = useCart();
  const ui = useUI();
  const { dispensary } = useDispensary();
  const user = useUser();
  const { open: openItemsUnavailableModal } = useItemsUnavailableModal();

  // Dutchie Pay checkout pay/instore route should not attempt to restore the cart
  const shouldSkip =
    !checkoutToken || !dispensary?.id || hasAttemptedRestore || (router?.pathname.includes('pay/instore') ?? false);
  const { data, error, loading } = useGetTrackedCartQuery({
    skip: shouldSkip,
    variables: {
      checkoutToken: checkoutToken as string,
      dispensaryId: dispensary?.id ?? '',
    },
    fetchPolicy: 'network-only',
  });

  const productIds = data?.getTrackedCart?.order ? getProductIdsFromOrderData(data.getTrackedCart.order) : [];

  const { data: productsData, error: productsError, loading: productsLoading } = useFilteredProductsQuery({
    skip: shouldSkip || !data?.getTrackedCart || productIds.length === 0,
    variables: {
      productsFilter: {
        productIds,
        dispensaryId: dispensary?.id,
        bypassOnlineThresholds: true,
        Status: 'All',
      },
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.getTrackedCart && !loading && !error && !productsLoading && !productsError && !hasAttemptedRestore) {
      const performRestoration = (): void => {
        try {
          if (!data.getTrackedCart?.order) {
            throw new Error('Invalid cart data');
          }

          if (!dispensary?.id) {
            throw new Error('Invalid dispensary data');
          }

          restoreCartFromTrackedData(cart, user, data.getTrackedCart);

          const products =
            productsData?.filteredProducts?.products.filter((p): p is NonNullable<typeof p> => p !== null) ?? [];
          const restorationResults = restoreCartItems(
            cart,
            dispensary,
            products as GqlProducts[],
            data.getTrackedCart.order
          );

          let message: string;
          let type: 'danger' | 'success' | 'warning';

          if (restorationResults.failedCount === 0) {
            message = `Cart restored successfully! ${String(restorationResults.restoredCount)} items added.`;
            type = 'success';
          } else if (restorationResults.restoredCount > 0) {
            message = `Cart partially restored: ${String(restorationResults.restoredCount)} items added, ${String(
              restorationResults.failedCount
            )} items unavailable.`;
            type = 'warning';
          } else {
            message = 'Unable to restore cart - all items are no longer available.';
            type = 'danger';
          }

          ui.showErnie(message, type);

          const cleanQuery = { ...router?.query };
          delete cleanQuery.checkoutToken;
          void router?.replace(
            {
              pathname: router.pathname,
              query: cleanQuery,
            },
            undefined,
            { shallow: true }
          );

          setHasAttemptedRestore(true);

          if (restorationResults.restoredCount > 0 && !ui.isMobileEcommApp) {
            setTimeout(() => {
              if (restorationResults.failedCount > 0) {
                void openItemsUnavailableModal({ failedCartItems: restorationResults.failedItems });
              } else {
                ui.openCartDrawer();
              }
            }, 100);
          }
        } catch (restoreError) {
          console.error('Failed to restore cart data:', restoreError);
          ui.showErnie('Failed to restore cart. Please try again.', 'danger');
          setHasAttemptedRestore(true);
        }
      };

      performRestoration();
    }

    if ((error || productsError) && !hasAttemptedRestore) {
      console.error('Failed to retrieve tracked cart or products:', error ?? productsError);
      ui.showErnie('Cart link expired or invalid. Please try again.', 'danger');
      setHasAttemptedRestore(true);
    }
  }, [
    data,
    error,
    loading,
    productsData,
    productsError,
    productsLoading,
    cart,
    ui,
    user,
    router,
    hasAttemptedRestore,
    dispensary,
    openItemsUnavailableModal,
  ]);

  return { loading, error, hasAttemptedRestore };
}
